/* Page d'article Maison Sirito.

   Quatre services, tous facultatifs : sans ce script la page reste lisible,
   seuls le sommaire, la jauge et le bouton « copier le lien » manquent.
   - sommaire construit à partir des <h2> du contenu, avec suivi de lecture ;
   - jauge de progression en haut de l'écran ;
   - copie du lien dans le presse-papiers ;
   - mise en boîte défilante des tableaux trop larges.
*/

(function initArticle() {
  const racine = document.querySelector('.sirito-article');
  if (!racine) return;

  const corps = racine.querySelector('[data-article-body]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
     Tableaux : un tableau de cinq colonnes déborde d'une colonne de 780px.
     On l'enferme dans une boîte qui défile seule, plutôt que de laisser la
     page entière glisser latéralement.
     --------------------------------------------------------------------- */
  if (corps) {
    corps.querySelectorAll('table').forEach((table) => {
      if (table.parentElement.classList.contains('sirito-article__table-wrap')) return;
      const boite = document.createElement('div');
      boite.className = 'sirito-article__table-wrap';
      table.parentNode.insertBefore(boite, table);
      boite.appendChild(table);
    });
  }

  /* ---------------------------------------------------------------------
     Sommaire
     --------------------------------------------------------------------- */
  const sommaire = racine.querySelector('[data-article-toc]');
  const liste = racine.querySelector('[data-article-toc-list]');
  let liens = [];
  let titres = [];

  if (corps && sommaire && liste) {
    titres = Array.from(corps.querySelectorAll('h2'));

    // Un sommaire d'une seule entrée n'apprend rien : on ne l'affiche qu'à
    // partir de deux sous-titres.
    if (titres.length >= 2) {
      const utilises = new Set();

      titres.forEach((titre, i) => {
        if (!titre.id) {
          const base =
            titre.textContent
              .toLowerCase()
              .normalize('NFD')
              .replace(/[̀-ͯ]/g, '')
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '')
              .slice(0, 60) || `section-${i + 1}`;
          let id = base;
          let n = 2;
          while (utilises.has(id) || document.getElementById(id)) {
            id = `${base}-${n}`;
            n += 1;
          }
          titre.id = id;
        }
        utilises.add(titre.id);

        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'sirito-article__toc-link sirito-focus';
        a.href = `#${titre.id}`;
        a.textContent = titre.textContent.trim();
        li.appendChild(a);
        liste.appendChild(li);
      });

      sommaire.hidden = false;
      liens = Array.from(liste.querySelectorAll('a'));

      liens.forEach((lien, i) => {
        lien.addEventListener('click', (event) => {
          event.preventDefault();
          const cible = titres[i];
          cible.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
          // Le lecteur d'écran doit suivre le regard : on emmène le focus sur
          // le titre atteint, sans l'ajouter à l'ordre de tabulation.
          cible.setAttribute('tabindex', '-1');
          cible.focus({ preventScroll: true });
          history.replaceState(null, '', `#${cible.id}`);
        });
      });
    }
  }

  /* ---------------------------------------------------------------------
     Suivi de lecture et jauge de progression

     Un seul écouteur de défilement, lissé par requestAnimationFrame : deux
     écouteurs séparés feraient deux fois le travail à chaque pixel.
     --------------------------------------------------------------------- */
  const jauge = racine.querySelector('.sirito-article__progress-bar');
  let actif = -1;
  let enAttente = false;

  function peindre() {
    enAttente = false;

    if (jauge && corps) {
      const boite = corps.getBoundingClientRect();
      const hauteurLue = window.innerHeight - boite.top;
      const total = boite.height;
      let part = total > 0 ? hauteurLue / total : 0;
      if (part < 0) part = 0;
      if (part > 1) part = 1;
      jauge.style.setProperty('--progress', part.toFixed(4));
    }

    if (liens.length) {
      // Le titre courant est le dernier passé sous la ligne des 120px.
      let index = 0;
      for (let i = 0; i < titres.length; i += 1) {
        if (titres[i].getBoundingClientRect().top <= 120) index = i;
        else break;
      }
      if (index !== actif) {
        if (liens[actif]) liens[actif].classList.remove('sirito-article__toc-link--active');
        liens[index].classList.add('sirito-article__toc-link--active');
        actif = index;
      }
    }
  }

  function planifier() {
    if (enAttente) return;
    enAttente = true;
    window.requestAnimationFrame(peindre);
  }

  if (jauge || liens.length) {
    window.addEventListener('scroll', planifier, { passive: true });
    window.addEventListener('resize', planifier);
    peindre();
  }

  /* ---------------------------------------------------------------------
     Typographie française

     La ponctuation double (; : ! ? « ») demande une espace avant, mais une
     espace ordinaire autorise un retour à la ligne : on voit alors un « ; »
     seul en tête de ligne, ce qui arrive vite dans une colonne étroite.

     On pose donc une espace insécable. L'espace fine (U+202F), plus juste du
     point de vue typographique, a été essayée puis abandonnée : Stevie Sans
     ne la dessine pas, le navigateur lui donne une chasse quasi nulle et la
     ponctuation vient coller au mot. L'insécable ordinaire est un peu large,
     mais elle se voit et elle tient la ligne.

     Le remplacement ne touche que les nœuds de texte : le balisage, les
     attributs, le code et les scripts restent intacts.
     --------------------------------------------------------------------- */
  const INSECABLE = String.fromCharCode(0x00a0);
  const SANS_TYPO = new Set(['SCRIPT', 'STYLE', 'PRE', 'CODE', 'TEXTAREA']);

  function espacesFines(zone) {
    if (!zone) return;
    const marcheur = document.createTreeWalker(zone, NodeFilter.SHOW_TEXT, {
      acceptNode(noeud) {
        return SANS_TYPO.has(noeud.parentNode.nodeName)
          ? NodeFilter.FILTER_REJECT
          : NodeFilter.FILTER_ACCEPT;
      },
    });
    const noeuds = [];
    while (marcheur.nextNode()) noeuds.push(marcheur.currentNode);
    noeuds.forEach((noeud) => {
      const avant = noeud.nodeValue;
      const apres = avant
        .replace(/ (?=[;:!?»])/g, INSECABLE)
        .replace(/« /g, '«' + INSECABLE);
      if (apres !== avant) noeud.nodeValue = apres;
    });
  }

  espacesFines(racine);

  /* ---------------------------------------------------------------------
     Copie du lien
     --------------------------------------------------------------------- */
  const bouton = racine.querySelector('[data-article-copy]');
  if (bouton) {
    const iconeIdle = bouton.querySelector('[data-article-copy-idle]');
    const iconeDone = bouton.querySelector('[data-article-copy-done]');
    const statut = racine.querySelector('[data-article-copy-status]');
    let minuterie = null;

    async function copier(texte) {
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(texte);
          return;
        } catch (refus) {
          /* Le presse-papiers asynchrone se refuse dans plusieurs cas très
             ordinaires : permission non accordée, onglet qui n'a pas le
             focus, iframe sans autorisation. Ce n'est pas une raison de
             renoncer — la méthode historique passe encore là où celle-ci
             échoue. On la tente donc avant d'annoncer un échec. */
        }
      }

      const zone = document.createElement('textarea');
      zone.value = texte;
      zone.setAttribute('readonly', '');
      zone.style.position = 'fixed';
      zone.style.top = '0';
      zone.style.opacity = '0';
      document.body.appendChild(zone);
      zone.select();
      const reussi = document.execCommand('copy');
      document.body.removeChild(zone);
      if (!reussi) throw new Error('copie refusée');
    }

    bouton.addEventListener('click', async () => {
      const url = bouton.dataset.url || window.location.href;
      try {
        await copier(url);
      } catch (erreur) {
        /* Le message d'échec s'efface comme celui de réussite : laissé en
           place, il resterait affiché jusqu'au rechargement de la page. */
        if (statut) statut.textContent = 'Copie impossible';
        window.clearTimeout(minuterie);
        minuterie = window.setTimeout(() => {
          if (statut) statut.textContent = '';
        }, 2600);
        return;
      }

      if (iconeIdle) iconeIdle.hidden = true;
      if (iconeDone) iconeDone.hidden = false;
      if (statut) statut.textContent = bouton.dataset.done || 'Lien copié';

      window.clearTimeout(minuterie);
      minuterie = window.setTimeout(() => {
        if (iconeIdle) iconeIdle.hidden = false;
        if (iconeDone) iconeDone.hidden = true;
        if (statut) statut.textContent = '';
      }, 2600);
    });
  }
})();
